<template>
  <div class="mt-4">
      <div class="w-full">
        <label class="text-left block font-bold">Student Info</label>
        <div class="grid grid-cols-3 gap-7">
          <TextField 
            class="pt-1.5" 
            :placeholder="'District Student ID'" 
            v-model="formValues.studentId" 
            @blur="checkStudent()">
            District Student ID
          </TextField>
          <ValidationProvider v-if="showStudentIdConfirm()" :rules="`required|matchDistStudIds:${formValues.studentId}`" name="district student ID confirmation" v-slot="{ errors }">
            <TextField  
              class="pt-1.5"
              v-model="formValues.studentIdConfirm"
              @blur="checkStudent()">
              Confirm District Student ID
            </TextField>
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text">
              {{ errors[0] }}
            </span>
          </ValidationProvider>
          <CalendarField 
            class="pt-1.5" 
            :min="new Date(1900, 0, 1).toISOString().slice(0, 10)" 
            :max="today()" v-model="formValues.dateOfBirth"
          >
            Date of Birth
          </CalendarField>
        </div>
        <div class="grid grid-cols-3 gap-7">
          <div>
            <ValidationProvider rules="required|nameCheck" name="first name" v-slot="{ errors }">
              <TextField 
                class="pb-2" 
                :placeholder="'First Name'" 
                v-model="formValues.firstName" 
                @blur="checkStudent()">
              </TextField>
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
          </div>
          <div>
            <ValidationProvider rules="nameCheck" name="middle name" v-slot="{ errors }">
              <TextField 
                class="pb-2" 
                :placeholder="'Middle Name'" 
                v-model="formValues.middleName" 
                @blur="checkStudent()">
              </TextField>
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
          </div>
          <div>
            <ValidationProvider rules="required|nameCheck" name="last name" v-slot="{ errors }">
              <TextField 
                class="pb-2" 
                :placeholder="'Last Name'" 
                v-model="formValues.lastName"
                @blur="checkStudent()">
              </TextField>
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
          </div>
        </div>
        <div class="grid grid-cols-3 gap-7">
          <SelectionField class="pb-2"
            v-model="formValues.gender"
            :options="genderList"
          >
            Gender
          </SelectionField>
          <ValidationProvider rules="required" name="language" v-slot="{ errors }">
            <SelectionField class="pb-2"
              v-model="formValues.language"
              :options="languageList"
            >
              Language
            </SelectionField>
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text">
              {{ errors[0] }}
            </span>
          </ValidationProvider>
          <ValidationProvider rules="required" name="grade" v-slot="{ errors }">
            <SelectionField
              v-model="formValues.grade"
              :options="gradeList"
              @input="onGradeChange()"
              @blur="checkStudent()">
              Grade
            </SelectionField>
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text">
              {{ errors[0] }}
            </span>
          </ValidationProvider>
          </div>
          <div class="grid grid-cols-3 gap-7">
            <div>
              <ValidationProvider rules="required" name="program type" v-slot="{ errors }">
                <SelectionField 
                  v-model="selectedProgramType"
                  :disabled="districtCode == null"
                  :options="programList"
                  :val="'id'"
                  :label="'description'"
                  @input="onProgramChange()"
                >
                  Program Type
                </SelectionField>
                <span
                  class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                  name="error-text">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
            <ValidationProvider v-if="selectedProgramType === 1" rules="required" name="custom program type" v-slot="{ errors }">
              <TextField
                v-model="formValues.customProgram"
                :placeholder="'Write the Program Type Here'"
              >
              <pre class="m-0"> </pre> <!-- Blank space label to fix style of TextField -->
              </TextField>
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
        </div>
      </div>
    <!-- defaultValue: {{ defaultValue }} -->

    <!-- formValues: {{ formValues }}  -->
    <!-- selectedProgramType: {{ selectedProgramType }} -->
    <Modal
      ref="suggested-student-modal"
      :id="suggestedStudentModal.id"
      :title="suggestedStudentModal.title"
      hide-footer
      size="lg" >
      <template #title>{{ suggestedStudentModal.title }}</template>
      <template #content>
        <suggested-student-form 
          id="SuggestedStudentForm" 
          :suggestedStudents="suggestedStudentModal.content"
          @edit="onEditSuggestedStudent"
          @ignoreSuggestions="onIgnoreSuggestions"
        />
      </template>
    </Modal>
  </div>
</template>

<script>
import DropdownLists from '../components/DropdownLists.json'
import SuggestedStudentForm from './SuggestedStudentList/SuggestedStudentForm.vue'
import Modal from '../components/Modal.vue'

import SelectionField from './formComponents/SelectionField.vue'
import TextField from './formComponents/TextField.vue'
import CalendarField from './formComponents/CalendarField.vue'

import requestFormService from '../services/formSubmission/requestForm.js'

import cloneDeep from 'clone-deep'
import { isEqual, isObject, isString, isNumber, isNil } from 'lodash'


export default {
  name: "StudentInfo",
  components: {
    SuggestedStudentForm,
    SelectionField,
    CalendarField,
    TextField,
    Modal,
  },
  props: {
    disableDefaultValueUpdates: Boolean,
    defaultValue: undefined,
    schoolYear: undefined,
    districtCode: undefined,
    partitions: undefined,
  },
  data() {
    return {
      formValues: {
        gender: '', 
        firstName: '', 
        lastName: '', 
        middleName: '', 
        grade: '', 
        studentId: '', 
        studentIdConfirm: '',
        dateOfBirth: '', 
        language: '', 
        marssCode: undefined, 
        dbId: '',
        program: '',
      },
      checkStudentApiBody: undefined,
      selectedProgramType: '',
      genderList: DropdownLists.genderList,
      languageList: DropdownLists.languageList,
      gradeList: DropdownLists.gradeList,
      programList: [],
      suggestedStudentModal: {
        id: 'suggested-student-modal',
        title: 'Possible Duplicate Student Detected',
        content: ''
      },
      ignoredIds: []
    }
  },
  computed: {},
  methods: {
    updateValue() {
      this.$emit('input', this.formValues)
    },
    within100Years: ({ value }) => new Date(value) >= new Date(1900, 0, 1),
    getFormat() {
      return /[`!@#$%^&*_+=[\]{};:"\\|<>/?~]/
    },
    showStudentIdConfirm() {
      if (this.formValues?.studentId?.length)
        return this.formValues?.studentId !== this.defaultValue?.studentId
      else{
        return false
      }
    },
    specialChar({ value }) {
      var format = /[`!@#$%^&*_+=[\]{};:"\\|<>/?~]/;
      if(format.test(value)) {
        console.log(value)
        console.log('special char detected')
      }
      return !format.test(value)
    },
    onSubmit(event) {
      event.preventDefault()
      alert(JSON.stringify(this.form))
    },
    onProgramChange() {
      this.$emit('programSelected', this.selectedProgramType);
    },
    onGradeChange() {
      this.$emit('gradeSelected', parseInt(this.formValues?.grade));
    },
    onEditSuggestedStudent(dbId) {
      //this.$bvModal.hide(this.suggestedStudentModal.id);
      this.$emit('editSuggestedStudent', dbId)
    },
    onIgnoreSuggestions(suggestedStudents) {
      for(let student of suggestedStudents) {
        this.ignoredIds.push(student.dbId)
      }

      this.$refs['suggested-student-modal'].closeModal()
    },
    today() {
      var date = new Date();
      var month = date.getMonth() + Number(1);
      if (month < 10) {
        month = '0'+month;
      }
      return date.getFullYear() +'-'+ month +'-'+ date.getDate()
    },
    setDefault() {
      this.formValues.language = 'en'
      if (this.defaultValue === undefined) {
        return
      }

      if (!this.checkSchema(this.defaultValue)) {
        return
      }

      if (this.disableDefaultValueUpdates) {
        return
      }

      this.selectedProgramType = this.defaultValue.program
      this.formValues = cloneDeep(this.defaultValue)

      this.$emit('defaultValueUsed')
    },
    checkSchema(obj) {
      if (!isObject(obj)) {
        console.warn(`Invalid default value data type in StudentInfo, ${typeof obj}`)
        return false
      }

      if (!(isNil(obj.gender) || isString(obj.gender))) {
        console.warn(`Invalid default value data type in StudentInfo.gender, ${typeof obj.gender}`)
        delete obj.gender
      }
      if (!(isNil(obj.firstName) || isString(obj.firstName))) {
        console.warn(`Invalid default value data type in StudentInfo.firstName, ${typeof obj.firstName}`)
        delete obj.firstName
      }
      if (!(isNil(obj.lastName) || isString(obj.lastName))) {
        console.warn(`Invalid default value data type in StudentInfo.lastName, ${typeof obj.lastName}`)
        delete obj.lastName
      }
      if (!(isNil(obj.middleName) || isString(obj.middleName))) {
        console.warn(`Invalid default value data type in StudentInfo.middleName, ${typeof obj.middleName}`)
        delete obj.middleName
      }
      if (!(isNil(obj.grade) || isNumber(obj.grade) || isString(obj.grade))) {
        console.warn(`Invalid default value data type in StudentInfo.grade, ${typeof obj.grade}`)
        delete obj.grade
      }
      if (!(isNil(obj.studentId) || isString(obj.studentId))) {
        console.warn(`Invalid default value data type in StudentInfo.studentId, ${typeof obj.studentId}`)
        delete obj.studentId
      }
      if (!(isNil(obj.dateOfBirth) || isString(obj.dateOfBirth))) {
        console.warn(`Invalid default value data type in StudentInfo.dateOfBirth, ${typeof obj.dateOfBirth}`)
        delete obj.dateOfBirth
      }
      if (!(isNil(obj.program) || (isString(obj.program) || isNumber(obj.program)))) {
        console.warn(`Invalid default value data type in StudentInfo.program, ${typeof obj.program}`)
        delete obj.program
      }
      if (!(isNil(obj.language) || isString(obj.language))) {
        console.warn(`Invalid default value data type in StudentInfo.language, ${typeof obj.language}`)
        delete obj.language
      }

      return true
    },
    async checkStudent() {
      if (this.schoolYear !== undefined) {
        let sid = this.formValues.studentId
        let fName = this.formValues.firstName
        let lName = this.formValues.lastName
        let grade = this.formValues.grade
        let schoolYear = this.schoolYear
        this.formValues['schoolYear'] = schoolYear
        if (this.districtCode && fName && lName && grade) {
          if (!sid) {
            this.formValues.studentId = null
          }
          let apiBody = {
            studentId: this.formValues.studentId,
            firstName: this.formValues.firstName,
            middleName: this.formValues.middleName,
            lastName: this.formValues.lastName,
            grade: this.formValues.grade,
            districtCode: this.districtCode,
            schoolYear: this.schoolYear,
          }

          if (isEqual(this.checkStudentApiBody, apiBody)) {
            return
          }
          this.checkStudentApiBody = apiBody

          try {
            let suggestedStudents = await requestFormService.postCheckStudent(apiBody)
            let studentMap = {}
            for (const student of suggestedStudents) {
              if (!(student.dbId in studentMap) && student.dbId !== this.defaultValue?.dbId) {
                studentMap[student.dbId] = student
              }
            }
            suggestedStudents = Object.values(studentMap)
            if (suggestedStudents.length > 0) {
              this.suggestStudent(suggestedStudents)
            }
          }
          catch {
            // do nothing
          }
        }
      }
    },
    suggestStudent(suggestedStudents) {
      let newSuggestedStudents = []
      newSuggestedStudents = suggestedStudents.filter(student => !this.ignoredIds.includes(student.dbId));

      if(newSuggestedStudents.length) {
        this.suggestedStudentModal.content = newSuggestedStudents
        this.$refs['suggested-student-modal'].openModal()
        //this.$root.$emit('bv::show::modal', this.suggestedStudentModal.id)
      }
    },
    getProgramList() {
      let queryParams = {}
      if (this.districtCode){
        queryParams.districtCode = this.districtCode
      } else {
        this.programList = []
        return
      }
      requestFormService.getProgramFromServer(queryParams)
        .then(result => {
          this.programList = result.data
        }, error => {
          console.error(error)
        })
    }
  },
  mounted() {
    this.setDefault();
    this.getProgramList()
    this.updateValue()
  },
  watch: {
    defaultValue() {
      this.setDefault()
    },
    formValues() {
      this.updateValue()
    },
    selectedProgramType(value) {
      this.formValues.program = value
    },
    districtCode() {
      this.getProgramList()
    }
  }
}
// https://stackoverflow.com/questions/47311936/v-model-and-child-components
</script>
<style>
  .student-info-form div .formulate-input {
    margin-bottom: 7px;
    margin-top: 7px;
  }
</style>
